import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Input, Label, Row, Nav, NavItem, NavLink, TabContent, TabPane, Button, Spinner, Alert, Table, Modal, ModalHeader, ModalBody, Form, FormGroup } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { getAppConfiguration } from '../../slices/appSettings/thunk';
import classnames from "classnames";
import { APIClient } from "../../helpers/api_helper";
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config';
import SweetAlert from 'react-bootstrap-sweetalert';
import BreadCrumb from '../../Components/Common/BreadCrumb';

const Reasons = () => {
    document.title = "Report Reasons";
    const [reasons, setReasons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [selectedReason, setSelectedReason] = useState(null);
    const [editedReason, setEditedReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [addModal, setAddModal] = useState(null);
    const [newReason, setNewReason] = useState('');
    const [addingSaving, setAddingSaving] = useState(false);
    const [deleteModal, setDeleteModal] = useState(null);
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [activeTab, setActiveTab] = useState("1");

    const tabs = [
        { id: "1", name: "Report Post", userType: "1" },
        { id: "2", name: "Report User", userType: "2" },
        { id: "3", name: "Delete Account", userType: "3" },
        { id: "4", name: "Subscription Cancel", userType: "4" },
        { id: "5", name: "Date Cancel", userType: "5" }
    ];

    // Move fetchReasons to a useCallback to prevent recreation
    const fetchReasons = useCallback(async (userType) => {
        setLoading(true);
        try {
            const authUser = JSON.parse(sessionStorage.getItem("authUser"));
            const response = await fetch(`${config.api.API_URL}python/reportReasons/?userType=${userType}`, {
                headers: {
                    'Authorization': authUser.token
                }
            });

            if (!response.ok) throw new Error('Failed to fetch reasons');

            const data = await response.json();
            setReasons(data.result);
        } catch (error) {
            toast.error('Failed to fetch reasons', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch reasons when tab changes
    useEffect(() => {
        const currentTab = tabs.find(tab => tab.id === activeTab);
        if (currentTab) {
            fetchReasons(currentTab.userType);
        }
    }, [activeTab]); // Only run when activeTab changes

    // Fetch single reason for editing
    const handleEdit = async (id) => {
        try {
            const currentTab = tabs.find(tab => tab.id === activeTab);
            const authUser = JSON.parse(sessionStorage.getItem("authUser"));
            const response = await fetch(
                `${config.api.API_URL}python/reportReasons/?userType=${currentTab.userType}&reportReasonId=${id}`,
                {
                    headers: {
                        'Authorization': authUser.token
                    }
                }
            );

            if (!response.ok) throw new Error('Failed to fetch reason details');

            const data = await response.json();
            if (response.status === 200 && data.result[0]) {
                setSelectedReason(data.result[0]);
                setEditedReason(data.result[0].reason.en);
                setEditModal(true);
            }
        } catch (error) {
            toast.error('Failed to load reason details', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
            console.error(error);
            console.error(error);
        }
    };

    // Update reason
    const handleUpdate = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const currentTab = tabs.find(tab => tab.id === activeTab);
            const authUser = JSON.parse(sessionStorage.getItem("authUser"));
            const response = await fetch(`${config.api.API_URL}python/updateReportReasons/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authUser.token
                },
                body: JSON.stringify({
                    reportReasonId: selectedReason.id,
                    userType: currentTab.userType,
                    reason: {
                        en: editedReason
                    }
                })
            });

            if (!response.ok) throw new Error('Failed to update reason');

            const data = await response.json();
            if (response.status === 200) {
                toast.success('Reason updated successfully', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
                setEditModal(false);
                fetchReasons(currentTab.userType);
            }
        } catch (error) {
            toast.error('Failed to update reason', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    // Handle showing Add modal
    const handleShowAdd = () => {
        setNewReason('');  // Reset the input
        setAddModalVisible(true);  // Show modal
    };

    // Handle Add confirmation
    const handleAddConfirm = async (currentTab) => {
        try {
            const authUser = JSON.parse(sessionStorage.getItem("authUser"));
            const response = await fetch(`${config.api.API_URL}python/addReportReasons/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authUser.token
                },
                body: JSON.stringify({
                    userType: currentTab.userType,
                    reason: {
                        en: newReason
                    }
                })
            });

            if (!response.ok) throw new Error('Failed to add reason');

            const data = await response.json();
            if (response.status === 200) {
                toast.success('Reason added successfully', {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        theme: "colored"
                    });
                setAddModal(null);
                setNewReason('');
                setAddModalVisible(false);
                fetchReasons(currentTab.userType);
            }
        } catch (error) {
            toast.error('Failed to add reason', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
            console.error(error);
            setAddModal(null);
        }
    };

    // Update the modal whenever newReason or visibility changes
    useEffect(() => {
        if (addModalVisible) {
            const currentTab = tabs.find(tab => tab.id === activeTab);
            setAddModal(
                <SweetAlert
                    title="Add New Reason"
                    showCancel
                    confirmBtnText="Add"
                    confirmBtnBsStyle="success"
                    cancelBtnBsStyle="danger"
                    onConfirm={() => {
                        if (!newReason.trim()) {
                            toast.error('Please enter a reason');
                return;
            }
                        handleAddConfirm(currentTab);
                    }}
                    onCancel={() => {
                        setAddModalVisible(false);
                        setAddModal(null);
                        setNewReason('');
                    }}
                >
                    <div className="mb-3">
                        <Label className="form-label">Reason</Label>
                        <input
                            type="text"
                            className="form-control"
                            value={newReason}
                            onChange={(e) => setNewReason(e.target.value)}
                            placeholder="Enter reason"
                            autoFocus
                        />
                    </div>
                </SweetAlert>
            );
        } else {
            setAddModal(null);
        }
    }, [addModalVisible, newReason, activeTab]);

    // Delete reason
    const handleDelete = (id) => {
        setDeleteModal(
            <SweetAlert
                title="Are you sure?"
                warning
                showCancel
                confirmBtnText="Yes, delete it!"
                confirmBtnBsStyle="success"
                cancelBtnBsStyle="danger"
                onConfirm={() => {
                    handleDeleteConfirm(id);
                }}
                onCancel={() => setDeleteModal(null)}
            >
                You won't be able to revert this!
            </SweetAlert>
        );
    };

    const handleDeleteConfirm = async (id) => {
        try {
            const currentTab = tabs.find(tab => tab.id === activeTab);
            const authUser = JSON.parse(sessionStorage.getItem("authUser"));
            const response = await fetch(`${config.api.API_URL}python/reportReasonsAction/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authUser.token
                },
                body: JSON.stringify({
                    reportReasonId: id,
                    deleteStatus: 2
                })
            });

            if (!response.ok) throw new Error('Failed to delete reason');

            const data = await response.json();
            if (response.status === 200) {
                toast.success('Reason deleted successfully', {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    theme: "colored"
                    });
                setDeleteModal(null);
                fetchReasons(currentTab.userType);
                }
            } catch (error) {
            toast.error('Failed to delete reason', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
            console.error(error);
            setDeleteModal(null);
        }
    };

    return (
        <div className="page-content">
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
            {deleteModal}
            {addModal}
            <Container fluid>
                <BreadCrumb title="Report Reasons" pageTitle="Settings" />
                <Row>
                    <Col>
                        <Card>
                            <CardHeader>
                                <Nav tabs className="nav-tabs-custom">
                                    {tabs.map((tab) => (
                                        <NavItem key={tab.id}>
                                            <NavLink
                                                className={classnames({ active: activeTab === tab.id })}
                                                onClick={() => setActiveTab(tab.id)}
                                            >
                                                {tab.name}
                                        </NavLink>
                                    </NavItem>
                                    ))}
                                </Nav>
                                            </CardHeader>
                                            <CardBody>
                                <TabContent activeTab={activeTab}>
                                    {tabs.map((tab) => (
                                        <TabPane key={tab.id} tabId={tab.id}>
                                            <div className="d-flex justify-content-end mb-3">
                                                <Button color="primary" onClick={handleShowAdd}>
                                                    Add New Reason
                                                                    </Button>
                                                                </div>
                                            {loading ? (
                                                <div className="text-center">
                                                    <Spinner>Loading...</Spinner>
                                                            </div>
                                            ) : (
                                                <Table responsive>
                                                    <thead>
                                                        <tr>
                                                            <th>SL No</th>
                                                            <th>Reason</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reasons.map((reason, index) => (
                                                            <tr key={reason.id}>
                                                                <td>{index + 1}</td>
                                                                <td>{reason.reason.en}</td>
                                                                <td>
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                                        className="me-2"
                                                                        onClick={() => handleEdit(reason.id)}
                                                                    >
                                                                        Edit
                                                        </Button>
                                                                                <Button
                                                                                    color="danger"
                                                                        size="sm"
                                                                        onClick={() => handleDelete(reason.id)}
                                                                    >
                                                                        Delete
                                                                                </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                                                            )}
                                        </TabPane>
                                                            ))}
                                </TabContent>
                                                    </CardBody>
                                                </Card>
                                                            </Col>
                                                        </Row>
            </Container>

            {/* Edit Modal */}
            <Modal isOpen={editModal} toggle={() => setEditModal(false)}>
                <ModalHeader toggle={() => setEditModal(false)}>
                    Edit Reason
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleUpdate}>
                        <FormGroup>
                            <Label for="reason">Reason</Label>
                                                                        <Input 
                                id="reason"
                                                                            type="text"
                                value={editedReason}
                                onChange={(e) => setEditedReason(e.target.value)}
                                required
                            />
                        </FormGroup>
                                                                        <Button 
                            color="primary" 
                            type="submit"
                            disabled={saving}
                        >
                            {saving ? <Spinner size="sm">Saving...</Spinner> : 'Save Changes'}
                                                                        </Button>
                    </Form>
                </ModalBody>
            </Modal>
        </div>
    );
};

export default Reasons;